import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { authAxios } from "../utils/auth";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft, Heart, User, Sparkles, Brain, Copy, Save
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import AchievementPanel, { STAGE_LABELS, STAGE_OBJECTIVES, STAGE_DETAILED } from "../components/AchievementPanel";
import RollbackModal from "../components/RollbackModal";
import CoachingPanel from "../components/CoachingPanel";
import ChatInput from "../components/ChatInput";
import MessageContent from "../components/MessageContent";

// Import extracted chat components
import {
  ChatHeader,
  ConnectionLevelCircle,
  ConnectionLevelModal,
  EscalationGuideSlideout,
  EscalationProgressBar,
  MediaSidebar,
  SessionList,
  PracticeFeedbackPanel,
  CoachAnnotations,
  ErosTipBubble,
  PracticeFeedbackBubble,
  RollbackButton,
  LEVEL_NAMES,
  SESSION_MODE_COLORS,
} from "../components/chat";

const ErosChat = () => {
  const navigate = useNavigate();
  const { matchId, sessionId: urlSessionId } = useParams();
  const [searchParams] = useSearchParams();
  const adviceMatchId = searchParams.get("adviceFor");  // Pre-select match for advice
  const { user } = useAuth();
  const { theme } = useTheme();
  // Theme styling now handled by CSS variables (divine-* classes auto-switch)
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [match, setMatch] = useState(null);
  const [matchImages, setMatchImages] = useState([]);  // All non-primary images for cycling
  const [leftImageIndex, setLeftImageIndex] = useState(0);  // Current left side image index
  const [rightImageIndex, setRightImageIndex] = useState(1);  // Current right side image index
  const [matchVideos, setMatchVideos] = useState([]);  // Videos for right side player
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);
  const [chatState, setChatState] = useState(null);
  const [previousChatState, setPreviousChatState] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sendMessageRef = useRef(null);
  const chatInputRef = useRef(null);  // Ref for ChatInput component
  const levelScrollRef = useRef(null);  // Ref for level modal scroll container
  const chatContainerRef = useRef(null);  // Ref for chat scroll container (mobile fade effect)
  
  // Message display optimization - only render last N messages initially
  const [visibleMessageCount, setVisibleMessageCount] = useState(30);
  const [isLoadingMore, setIsLoadingMore] = useState(false);  // Track auto-loading state
  const MESSAGES_PER_LOAD = 30;
  
  // Session management state
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(true);
  const [showSessionList, setShowSessionList] = useState(!urlSessionId); // Hide session list if URL has session
  
  // V2 Practice Mode state
  const [useV2, setUseV2] = useState(true);  // V2 is default
  const [practiceMode, setPracticeMode] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState("");
  const [practiceScenarios, setPracticeScenarios] = useState([]);
  const [lastPracticeFeedback, setLastPracticeFeedback] = useState(null);
  const [showCoachAnnotations, setShowCoachAnnotations] = useState(true);  // Eros Coach toggle (enables coach features) - ON by default
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);  // Track mobile viewport for background styling
  
  // V2 Tips toggle (coach_v2) - persist in localStorage
  const [showV2TipsInChat, setShowV2TipsInChat] = useState(() => {
    const saved = localStorage.getItem("eros_show_v2_tips_in_chat");
    return saved !== "false";  // Default to true
  });
  
  // V1 Tips toggle (coach_overlay_agent) - persist in localStorage
  const [showV1TipsInChat, setShowV1TipsInChat] = useState(() => {
    const saved = localStorage.getItem("eros_show_v1_tips_in_chat");
    return saved !== "false";  // Default to true for backwards compatibility
  });
  
  const [maxWords, setMaxWords] = useState(75); // Default max words for persona messages
  
  // Model selection state - null means "use admin panel default"
  const [selectedModel, setSelectedModel] = useState(null);  // Default to admin panel assignment
  const AVAILABLE_MODELS = [
    { provider: null, name: null, label: "Admin Default" },  // Use dynamic assignment from admin panel
    { provider: "anthropic", name: "claude-sonnet-4-5-20250929", label: "Claude Sonnet 4" },
    { provider: "openai", name: "gpt-5.2", label: "GPT-5.2" },
    { provider: "openai", name: "gpt-4o-mini", label: "GPT-4o-mini" },
    { provider: "gemini", name: "gemini-3-flash-preview", label: "Gemini 3 Flash" },
    { provider: "groq", name: "llama-3.3-70b-versatile", label: "Llama 3.3 70B (Groq)" },
    { provider: "groq", name: "llama-3.1-8b-instant", label: "Llama 3.1 8B (Groq)" },
  ];
  
  // Advice chat settings (for non-match chat)
  const [adviceResponseLength, setAdviceResponseLength] = useState(150);
  const [adviceUseUserData, setAdviceUseUserData] = useState(true);  // Default ON for personalized advice
  const [selectedMatchForAdvice, setSelectedMatchForAdvice] = useState(null);
  const [allMatches, setAllMatches] = useState([]);
  
  // Coaching Panel state
  const [coachingPanelOpen, setCoachingPanelOpen] = useState(false);
  const [selectedMessageForCoaching, setSelectedMessageForCoaching] = useState(null);
  const [currentDraft, setCurrentDraft] = useState("");  // Captured when coach opens
  
  // Token tracking for session
  const [sessionTokens, setSessionTokens] = useState(0);
  const [sessionCost, setSessionCost] = useState(0);
  const [coachingTokens, setCoachingTokens] = useState(0);
  const [coachingCost, setCoachingCost] = useState(0);
  
  // Escalation Game state (shared across escalation mode sessions for this relationship)
  const [escalationState, setEscalationState] = useState(null);
  const [showObjectiveSlideout, setShowObjectiveSlideout] = useState(false);  // Escalation slideout panel
  const [compactProgressMode, setCompactProgressMode] = useState(false);  // Compact circular progress toggle
  const [showLevelModal, setShowLevelModal] = useState(false);  // Connection level info modal
  const [driftInfo, setDriftInfo] = useState({ drift_bonus: 0, effective_level: 0, vibe: "neutral" });  // Boundary drift info
  
  // Session mode state
  const [showModeSelector, setShowModeSelector] = useState(false);
  const [currentSessionMode, setCurrentSessionMode] = useState("regular");
  const [modeSelectorStep, setModeSelectorStep] = useState("mode"); // "mode", "difficulty", "scenario", "advice_sub", "dating_sub"
  const [selectedModeForSubOptions, setSelectedModeForSubOptions] = useState(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState("medium"); // "easy", "medium", "hard"
  const [currentSessionContext, setCurrentSessionContext] = useState("advice"); // For advice chat context types
  
  // Mode-specific placeholder text for the chat input
  const getPlaceholderText = () => {
    if (matchId) {
      return `Message ${match?.name || "Persona"}...`;
    }
    
    // Context-specific placeholders for advice chat modes
    const placeholders = {
      "advice_direct": "Ask any relationship question...",
      "advice_coaching": "Tell me about your situation for guided coaching...",
      "social": "Paste a post or describe what you want to comment on...",
      "dating_bio": "Paste your bio or describe what vibe you're going for...",
      "dating_img": "Describe your photo or what you want feedback on...",
      "advice": "Ask Eros for advice..."
    };
    
    return placeholders[currentSessionContext] || "Ask Eros for advice...";
  };
  
  // Rollback state
  const [rollbackCredits, setRollbackCredits] = useState(3);
  const [checkpoints, setCheckpoints] = useState([]);
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [selectedCheckpoint, setSelectedCheckpoint] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  
  // V2 System state (coach_version + escalator_mode) - persist in localStorage
  const [v2SystemEnabled, setV2SystemEnabled] = useState(() => {
    const saved = localStorage.getItem("eros_v2_system_enabled");
    return saved !== "false";  // Default to true if not set, but respect explicit "false"
  });
  
  // Achievement tracking for header display
  const [inProgressAchievements, setInProgressAchievements] = useState([]);
  const [achievementDisplayIndex, setAchievementDisplayIndex] = useState(0);

  // Fetch actual token/cost from plog when session changes
  useEffect(() => {
    const fetchSessionStats = async () => {
      if (!currentSessionId) {
        setSessionTokens(0);
        setSessionCost(0);
        setCoachingTokens(0);
        setCoachingCost(0);
        return;
      }
      
      try {
        // Fetch chat session stats
        const response = await authAxios.get(`/admin/plog/session/${currentSessionId}/summary`);
        if (response.data) {
          setSessionTokens(response.data.total_tokens || 0);
          setSessionCost(response.data.total_cost || 0);
        }
      } catch (error) {
        // Fallback to client-side estimate if plog API fails (e.g., non-admin user)
        const totalChars = messages.reduce((sum, msg) => sum + (msg.content?.length || 0), 0);
        const estimatedTokens = Math.round(totalChars / 4);
        setSessionTokens(estimatedTokens);
        const estimatedCost = (estimatedTokens / 1000) * 0.003;
        setSessionCost(estimatedCost);
      }
      
      // Fetch coaching stats for this chat session
      try {
        const coachingResponse = await authAxios.get(`/v2/coaching/by-chat-session/${currentSessionId}/stats`);
        if (coachingResponse.data && coachingResponse.data.has_coaching) {
          setCoachingTokens(coachingResponse.data.total_tokens || 0);
          setCoachingCost(coachingResponse.data.estimated_cost || 0);
        } else {
          setCoachingTokens(0);
          setCoachingCost(0);
        }
      } catch (error) {
        // Coaching stats not available, reset to 0
        setCoachingTokens(0);
        setCoachingCost(0);
      }
    };
    
    fetchSessionStats();
  }, [currentSessionId, messages.length]);

  // Callback for coaching panel to notify token updates
  const handleCoachingTokensUpdate = (tokens, cost) => {
    setCoachingTokens(tokens);
    setCoachingCost(cost);
  };

  // Auto-scroll to current level when modal opens
  useEffect(() => {
    if (showLevelModal) {
      // Determine the current level to scroll to
      const currentLevel = currentSessionMode === "escalation" && escalationState 
        ? escalationState.level 
        : match?.persona_connection || 0;
      
      // Use multiple retries with increasing delays to handle dialog animation
      const attemptScroll = (attempts = 0) => {
        const container = levelScrollRef.current;
        if (!container && attempts < 5) {
          setTimeout(() => attemptScroll(attempts + 1), 50);
          return;
        }
        
        if (container) {
          const levelElement = container.querySelector(`[data-level="${currentLevel}"]`);
          if (levelElement) {
            // Calculate position by summing heights of previous siblings
            let scrollPos = 0;
            let sibling = levelElement.previousElementSibling;
            while (sibling) {
              scrollPos += sibling.offsetHeight + 8; // 8px is space-y-2 gap
              sibling = sibling.previousElementSibling;
            }
            container.scrollTop = scrollPos;
          }
        }
      };
      
      // Start after dialog has time to render
      setTimeout(attemptScroll, 200);
    }
  }, [showLevelModal, currentSessionMode, escalationState, match?.persona_connection]);

  useEffect(() => {
    // Fetch practice scenarios when matchId is available
    if (matchId) {
      fetchPracticeScenarios();
    }
  }, [matchId]);

  const fetchPracticeScenarios = async () => {
    try {
      const response = await authAxios.get("/v2/persona/practice/scenarios");
      setPracticeScenarios(response.data.scenarios || []);
    } catch (error) {
      console.error("Error fetching scenarios:", error);
    }
  };

  // Memoize sorted practice scenarios to avoid re-sorting on every render
  const sortedPracticeScenarios = useMemo(() => {
    return [...practiceScenarios].sort((a, b) => {
      const order = { easy: 1, medium: 2, hard: 3 };
      return (order[a.difficulty] || 2) - (order[b.difficulty] || 2);
    });
  }, [practiceScenarios]);

  // Memoize the highest in-progress achievement display
  const currentAchievementDisplay = useMemo(() => {
    if (inProgressAchievements.length === 0) return null;
    return inProgressAchievements[achievementDisplayIndex % inProgressAchievements.length];
  }, [inProgressAchievements, achievementDisplayIndex]);

  // Memoize lastAssistantIndex to avoid O(n²) recalculation in message rendering
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  }, [messages]);

  // Memoize visible messages for pagination - only render last N messages
  const visibleMessages = useMemo(() => {
    if (messages.length <= visibleMessageCount) {
      return { messages: messages, startIndex: 0, hasMore: false };
    }
    const startIndex = messages.length - visibleMessageCount;
    return {
      messages: messages.slice(startIndex),
      startIndex: startIndex,
      hasMore: startIndex > 0
    };
  }, [messages, visibleMessageCount]);

  // Load more messages handler (used by auto-scroll)
  const loadMoreMessages = useCallback(() => {
    if (isLoadingMore || !visibleMessages.hasMore) return;
    
    setIsLoadingMore(true);
    // Small delay to show loading state and prevent rapid firing
    setTimeout(() => {
      setVisibleMessageCount(prev => prev + MESSAGES_PER_LOAD);
      setIsLoadingMore(false);
    }, 300);
  }, [isLoadingMore, visibleMessages.hasMore]);

  // Ref for the load trigger element at top of messages
  const loadTriggerRef = useRef(null);

  // Auto-load more messages when trigger element becomes visible (Intersection Observer)
  useEffect(() => {
    const trigger = loadTriggerRef.current;
    if (!trigger || !visibleMessages.hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && visibleMessages.hasMore && !isLoadingMore) {
          const container = chatContainerRef.current;
          const prevScrollHeight = container?.scrollHeight || 0;
          
          loadMoreMessages();
          
          // After state update, adjust scroll position to maintain view
          if (container) {
            requestAnimationFrame(() => {
              const newScrollHeight = container.scrollHeight;
              const scrollDiff = newScrollHeight - prevScrollHeight;
              if (scrollDiff > 0) {
                container.scrollTop = scrollDiff;
              }
            });
          }
        }
      },
      {
        root: chatContainerRef.current,
        rootMargin: '100px 0px 0px 0px', // Trigger 100px before reaching top
        threshold: 0
      }
    );

    observer.observe(trigger);
    return () => observer.disconnect();
  }, [visibleMessages.hasMore, isLoadingMore, loadMoreMessages]);

  // Reset visible count when session changes
  useEffect(() => {
    setVisibleMessageCount(30);
    setIsLoadingMore(false);
  }, [currentSessionId]);

  // Track mobile viewport for responsive styling
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memoize selectedMatchData to avoid repeated .find() in render
  const selectedMatchData = useMemo(() => {
    if (!selectedMatchForAdvice || !allMatches.length) return null;
    return allMatches.find(m => m.match_id === selectedMatchForAdvice);
  }, [selectedMatchForAdvice, allMatches]);

  // Memoize match's first name to avoid repeated split operations
  const matchFirstName = useMemo(() => {
    return match?.name?.split(' ')[0] || 'Persona';
  }, [match?.name]);

  // Memoize practiceScenarios lookup maps for O(1) access
  const scenarioNameMap = useMemo(() => {
    const map = {};
    practiceScenarios.forEach(s => {
      map[s.scenario_id] = s.name;
      if (s.id) map[s.id] = s.name; // Also map by id for legacy references
    });
    return map;
  }, [practiceScenarios]);

  const scenarioDescMap = useMemo(() => {
    const map = {};
    practiceScenarios.forEach(s => {
      map[s.scenario_id] = s.description;
    });
    return map;
  }, [practiceScenarios]);

  // Memoize selected scenario description for practice mode header
  const selectedScenarioDesc = useMemo(() => {
    return selectedScenario ? scenarioDescMap[selectedScenario] : null;
  }, [selectedScenario, scenarioDescMap]);

  const fetchEscalationState = async () => {
    if (!matchId) return;
    try {
      const response = await authAxios.get(`/escalation/state/${matchId}`);
      setEscalationState(response.data);
      setRollbackCredits(response.data.rollback_credits || 3);
      // Update drift info from escalation state
      if (response.data.drift_info) {
        setDriftInfo(response.data.drift_info);
      }
    } catch (error) {
      console.error("Error fetching escalation state:", error);
    }
  };

  // Fetch drift info for non-escalation mode
  const fetchDriftInfo = async () => {
    if (!matchId) return;
    try {
      const response = await authAxios.get(`/escalation/drift-info/${matchId}`);
      setDriftInfo(response.data);
    } catch (error) {
      console.error("Error fetching drift info:", error);
    }
  };

  const fetchAchievementsForHeader = async () => {
    if (!matchId) return;
    try {
      const response = await authAxios.get(`/escalation/achievements/${matchId}`);
      const inProgress = response.data.in_progress || [];
      
      // Find highest progress achievements
      if (inProgress.length > 0) {
        const maxProgress = Math.max(...inProgress.map(a => a.progress));
        const highestAchievements = inProgress.filter(a => a.progress === maxProgress);
        setInProgressAchievements(highestAchievements);
      } else {
        setInProgressAchievements([]);
      }
    } catch (error) {
      console.error("Error fetching achievements for header:", error);
    }
  };

  // Helper function to replace generic persona references with actual match name
  const personalizeGuideText = (text) => {
    if (!text || !matchFirstName) return text;
    return text
      .replace(/\bthe persona\b/gi, matchFirstName)
      .replace(/\bpersona\b/gi, matchFirstName)
      .replace(/\bthem\b(?!\s+up)/gi, matchFirstName)  // Don't replace "them up"
      .replace(/\bthey're\b/gi, `${matchFirstName}'s`)
      .replace(/\btheir\b/gi, `${matchFirstName}'s`)
      .replace(/\bThey are\b/g, `${matchFirstName} is`);
  };

  // Store co-creative metadata for escalation processing
  const [lastCoCreativeMetadata, setLastCoCreativeMetadata] = useState({});
  
  const processEscalationGame = async (sessionId, coCreativeMetadata = {}) => {
    // Only process for escalation mode sessions
    if (!matchId || currentSessionMode !== "escalation") {
      return;
    }
    try {
      // Show processing indicator in state
      setEscalationState(prev => prev ? {...prev, processing: true} : prev);
      
      const response = await authAxios.post("/escalation/process", {
        relationship_id: matchId,
        session_id: sessionId,
        // Co-creative input tracking
        user_had_draft: coCreativeMetadata.userHadDraft || false,
        used_suggestion_verbatim: coCreativeMetadata.usedSuggestionVerbatim || false,
        last_user_message: coCreativeMetadata.lastUserMessage || null
      });
      
      // Update state with new values
      setEscalationState({...response.data, processing: false});
      setRollbackCredits(response.data.rollback_credits || 3);
      
      // Show XP change toast
      if (response.data.last_xp_delta) {
        const delta = response.data.last_xp_delta;
        const sign = delta > 0 ? '+' : '';
        toast.success(`XP ${sign}${delta.toFixed(1)} → ${response.data.xp.toFixed(1)}%`, {
          duration: 3000
        });
      }
      
      // Show level change notification
      if (escalationState && response.data.level !== escalationState.level) {
        const direction = response.data.level > escalationState.level ? "up" : "down";
        toast.success(`Level ${direction}! Now at ${response.data.level_label}`);
        if (direction === "up") {
          toast.success("+1 Rollback credit earned! (Level Up)");
        }
      }
      
      // Fetch updated checkpoints
      if (sessionId) {
        fetchCheckpoints(sessionId);
      }
    } catch (error) {
      console.error("Error processing escalation:", error);
      setEscalationState(prev => prev ? {...prev, processing: false} : prev);
    }
  };

  const fetchCheckpoints = async (sessionId) => {
    if (!matchId || !sessionId) return;
    try {
      const response = await authAxios.get(`/escalation/checkpoints/${sessionId}?relationship_id=${matchId}`);
      setCheckpoints(response.data.checkpoints || []);
      setRollbackCredits(response.data.rollback_credits || 3);
    } catch (error) {
      console.error("Error fetching checkpoints:", error);
    }
  };

  const openRollbackModal = (checkpoint) => {
    setSelectedCheckpoint(checkpoint);
    setRollbackModalOpen(true);
  };

  const executeRollback = async () => {
    if (!selectedCheckpoint || !currentSessionId || !matchId) return;
    
    setRollbackLoading(true);
    try {
      const response = await authAxios.post("/escalation/rollback", {
        checkpoint_id: selectedCheckpoint.checkpoint_id,
        relationship_id: matchId,
        session_id: currentSessionId
      });
      
      if (response.data.success) {
        toast.success(`Rolled back! Credits remaining: ${response.data.rollback_credits_remaining}`);
        setRollbackCredits(response.data.rollback_credits_remaining);
        setRollbackModalOpen(false);
        setSelectedCheckpoint(null);
        
        // Refresh messages and state by reloading the session
        await loadSession(currentSessionId);
        await fetchEscalationState();
        await fetchCheckpoints(currentSessionId);
      }
    } catch (error) {
      console.error("Error executing rollback:", error);
      toast.error(error.response?.data?.detail || "Failed to rollback");
    } finally {
      setRollbackLoading(false);
    }
  };

  // Find checkpoint for a message by index
  const getCheckpointForMessage = (messageIndex) => {
    return checkpoints.find(cp => cp.message_index === messageIndex);
  };

  // Handle V2 System toggle - updates session config AND persists to localStorage
  const handleV2SystemToggle = async (enabled) => {
    // Always persist to localStorage first (so it survives session changes)
    localStorage.setItem("eros_v2_system_enabled", enabled.toString());
    setV2SystemEnabled(enabled);
    
    if (!currentSessionId) {
      // No active session - just update local state (already done above)
      toast.success(`V2 Coaching ${enabled ? "enabled" : "disabled"}`);
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append("session_id", currentSessionId);
      params.append("coach_version", enabled ? "v2" : "v1");
      
      // If escalation mode, also toggle escalator_mode
      if (currentSessionMode === "escalation") {
        params.append("escalator_mode", enabled ? "shadow_v2" : "v1");
      }
      
      await authAxios.post(`/v2/session/config?${params.toString()}`);
      
      const modeText = currentSessionMode === "escalation" 
        ? "V2 Coaching + Escalator" 
        : "V2 Coaching";
      
      toast.success(`${modeText} ${enabled ? "enabled" : "disabled"}`);
    } catch (error) {
      console.error("Error updating session config:", error);
      toast.error("Failed to update session config (local toggle preserved)");
      // Don't revert - localStorage is already set, so user preference is preserved
    }
  };

  // Fetch session config to sync V2 state (localStorage takes priority as user preference)
  const fetchSessionConfig = async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await authAxios.get(`/v2/session/config?session_id=${sessionId}`);
      const config = response.data.session_config;
      if (config && config.coach_version) {
        // Session has explicit config - sync localStorage with it for this session
        const isV2 = config.coach_version === "v2" || config.coach_version === "both";
        localStorage.setItem("eros_v2_system_enabled", isV2.toString());
        setV2SystemEnabled(isV2);
      }
      // If no config.coach_version, keep localStorage value (user preference persists)
    } catch (error) {
      // Session config doesn't exist - use localStorage preference (already loaded in useState)
      console.debug("No session config found, using localStorage preference");
    }
  };

  // Combined init fetch - replaces 7 separate API calls with 1
  const fetchChatInit = async () => {
    if (!matchId) return;
    try {
      const response = await authAxios.get(`/chat-init/${matchId}`);
      const data = response.data;
      
      // Set all state from combined response
      setMatch(data.match);
      
      // Process images - support both old (data) and new (src) formats
      const allMedia = data.images || [];
      const videos = allMedia.filter(m => {
        const src = m.src || m.data || "";
        const isVideo = m.media_type === "video" || src.includes("video");
        // Only include videos explicitly marked for chat playback (starred)
        return isVideo && m.show_in_chat === true;
      });
      const images = allMedia.filter(m => {
        const src = m.src || m.data || "";
        return m.media_type !== "video" && !src.includes("video");
      });
      const nonPrimaryImages = images.filter(img => !img.is_primary);
      
      setMatchImages(nonPrimaryImages); // Keep ALL images for cycling
      setLeftImageIndex(0);
      setRightImageIndex(nonPrimaryImages.length > 1 ? 1 : 0);
      setMatchVideos(videos);
      if (videos.length > 0) {
        setCurrentVideoIndex(0);
      }
      
      // Set sessions
      setSessions(data.sessions || []);
      setLoadingSessions(false);
      
      // Set escalation state
      if (data.escalation_state) {
        setEscalationState(data.escalation_state);
        setRollbackCredits(data.escalation_state.rollback_credits || 3);
      }
      
      // Set drift info
      if (data.drift_info) {
        setDriftInfo(data.drift_info);
      }
      
      // Set achievements
      if (data.achievements) {
        setInProgressAchievements(data.achievements);
      }
    } catch (error) {
      console.error("Error fetching chat init, falling back to individual calls:", error);
      // Fallback to individual calls
      fetchMatch();
      fetchMatchImages();
      fetchSessions();
      fetchEscalationState();
      fetchDriftInfo();
      fetchAchievementsForHeader();
    }
  };

  // Persist V2 tips setting
  useEffect(() => {
    localStorage.setItem("eros_show_v2_tips_in_chat", showV2TipsInChat.toString());
  }, [showV2TipsInChat]);
  
  // Persist V1 tips setting
  useEffect(() => {
    localStorage.setItem("eros_show_v1_tips_in_chat", showV1TipsInChat.toString());
  }, [showV1TipsInChat]);

  useEffect(() => {
    if (matchId) {
      fetchChatInit();  // Single combined API call
    } else {
      // General chat (no matchId)
      fetchSessions();
      fetchAllMatches();  // Fetch matches for advice chat dropdown
    }
  }, [matchId]);

  // Load session from URL if provided
  useEffect(() => {
    if (urlSessionId && !loadingSessions && sessions.length > 0) {
      const sessionExists = sessions.find(s => s.session_id === urlSessionId);
      if (sessionExists) {
        loadSession(urlSessionId);
      }
    }
  }, [urlSessionId, loadingSessions, sessions]);

  // Update URL when session changes
  const updateUrlWithSession = (sessionId) => {
    if (sessionId) {
      if (matchId) {
        navigate(`/chat/${matchId}/${sessionId}`, { replace: true });
      } else {
        navigate(`/chat/session/${sessionId}`, { replace: true });
      }
    }
  };

  // Fetch all matches for the advice chat match selector
  const fetchAllMatches = async () => {
    try {
      const response = await authAxios.get("/matches");
      setAllMatches(response.data || []);
    } catch (error) {
      console.error("Error fetching matches:", error);
    }
  };

  // Pre-select match for advice when adviceFor URL param is provided
  useEffect(() => {
    if (adviceMatchId && !matchId && allMatches.length > 0) {
      // Verify the match exists
      const matchExists = allMatches.find(m => m.match_id === adviceMatchId);
      if (matchExists) {
        setSelectedMatchForAdvice(adviceMatchId);
      }
    }
  }, [adviceMatchId, matchId, allMatches]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchMatch = async () => {
    try {
      const response = await authAxios.get(`/matches/${matchId}`);
      setMatch(response.data);
    } catch (error) {
      console.error("Error fetching match:", error);
    }
  };

  const fetchMatchImages = async () => {
    if (!matchId) return;
    try {
      const response = await authAxios.get(`/matches/${matchId}/images`);
      const allMedia = response.data.images || [];
      
      // Separate videos and images - support both old (data) and new (src) formats
      // Only include videos explicitly marked for chat playback (starred)
      const videos = allMedia.filter(m => {
        const src = m.src || m.data || "";
        const isVideo = m.media_type === "video" || src.includes("video");
        return isVideo && m.show_in_chat === true;
      });
      const images = allMedia.filter(m => {
        const src = m.src || m.data || "";
        return m.media_type !== "video" && !src.includes("video");
      });
      
      // Filter to get only non-primary images for side display
      const nonPrimaryImages = images.filter(img => !img.is_primary);
      setMatchImages(nonPrimaryImages); // Keep ALL images for cycling
      setLeftImageIndex(0);
      setRightImageIndex(nonPrimaryImages.length > 1 ? 1 : 0);
      
      // Set videos for right side player (only those starred for chat)
      setMatchVideos(videos);
      // Don't reset video index here - let pendingMediaState handle restoration
    } catch (error) {
      console.error("Error fetching match images:", error);
    }
  };

  const fetchSessions = async () => {
    setLoadingSessions(true);
    try {
      // For match chat, filter by match_id; for advice chat, get all non-match sessions
      const params = matchId 
        ? { match_id: matchId, context: "persona_chat" }
        : {};  // Empty params to get all non-match sessions
      const response = await authAxios.get("/chat-sessions", { params });
      let allSessions = response.data;
      
      // For Eros Chat (no matchId), also fetch coaching sessions
      if (!matchId) {
        try {
          const coachingResponse = await authAxios.get("/v2/coaching/sessions");
          const coachingSessions = coachingResponse.data.map(cs => {
            // Parse title - backend already stores "PersonaName : ParentTitle"
            // Just add the 🏹 prefix, don't duplicate the name
            const displayTitle = cs.title || `${cs.match_name || 'Coaching'}`;
            
            return {
              session_id: cs.coaching_session_id,
              title: `🏹 ${displayTitle}`,
              context: "coaching_session",
              message_count: cs.message_count,
              created_at: cs.created_at,
              updated_at: cs.updated_at,
              // Store original coaching session data for linking
              coaching_session_id: cs.coaching_session_id,
              linked_chat_session_id: cs.chat_session_id,
              match_id: cs.match_id,
              match_name: cs.match_name,
              is_coaching_session: true  // Flag to handle differently in loadSession
            };
          });
          
          // Merge and sort by updated_at
          allSessions = [...allSessions, ...coachingSessions].sort((a, b) => 
            new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
          );
        } catch (coachErr) {
          console.warn("Failed to fetch coaching sessions:", coachErr);
        }
      }
      
      setSessions(allSessions);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const createNewSession = async (mode = "regular", difficulty = "medium", scenarioId = null, contextType = null) => {
    try {
      // Determine context based on matchId and contextType
      // If contextType is explicitly passed, use it; otherwise default based on matchId
      let sessionContext = contextType || (matchId ? "persona_chat" : "advice");
      
      const response = await authAxios.post("/chat-sessions", {
        match_id: matchId || null,
        context: sessionContext,
        mode: mode,  // "regular", "escalation", "practice"
        difficulty: mode === "escalation" ? difficulty : null,
        scenario_id: mode === "practice" ? scenarioId : null,
        advice_match_id: !matchId ? selectedMatchForAdvice : null  // Pass selected persona for advice context
      });
      
      const newSession = response.data;
      setSessions(prev => [newSession, ...prev]);
      setCurrentSessionId(newSession.session_id);
      setCurrentSessionMode(mode);
      setCurrentSessionContext(sessionContext);
      setSelectedDifficulty(difficulty);
      // Set practice mode and scenario based on session mode
      setPracticeMode(mode === "practice");
      setSelectedScenario(scenarioId || "");
      setMessages([]);
      setShowSessionList(false);
      setShowModeSelector(false);
      setModeSelectorStep("mode");
      setSelectedModeForSubOptions(null);
      
      // Enable V2 coaching for coaching sessions
      if (sessionContext === "advice_coaching") {
        setShowCoachAnnotations(true);
        setShowV2TipsInChat(true);
      }
      
      // Context-specific labels for toast
      const contextLabels = {
        "advice_direct": "Direct Chat",
        "advice_coaching": "Agent Eros Advice",
        "social": "Social AI",
        "dating_bio": "Bio Writer",
        "dating_img": "Image Analyst",
        "advice": "Advice Chat",
        "persona_chat": "Persona Chat"
      };
      
      const modeLabels = { regular: "Regular", escalation: "Escalation", practice: "Practice" };
      const difficultyLabels = { easy: "Easy", medium: "Medium", hard: "Hard" };
      const scenarioName = scenarioNameMap[scenarioId];
      
      if (mode === "escalation") {
        toast.success(`${modeLabels[mode]} (${difficultyLabels[difficulty]}) started!`);
      } else if (mode === "practice" && scenarioName) {
        toast.success(`${modeLabels[mode]}: ${scenarioName} started!`);
      } else if (contextLabels[sessionContext]) {
        toast.success(`${contextLabels[sessionContext]} started!`);
      } else {
        toast.success(`${modeLabels[mode]} conversation started!`);
      }
      
      // Fetch escalation state if escalation mode
      if (mode === "escalation" && matchId) {
        fetchEscalationState();
      }
    } catch (error) {
      console.error("Error creating session:", error);
      toast.error("Failed to create new session");
    }
  };

  const handleModeSelect = (mode) => {
    if (mode === "regular") {
      createNewSession("regular");
    } else if (mode === "escalation") {
      setSelectedModeForSubOptions("escalation");
      setModeSelectorStep("difficulty");
    } else if (mode === "practice") {
      setSelectedModeForSubOptions("practice");
      setModeSelectorStep("scenario");
    }
  };

  // Advice chat mode handlers (for non-match chat)
  const handleAdviceModeSelect = (mode) => {
    if (mode === "advice") {
      setSelectedModeForSubOptions("advice");
      setModeSelectorStep("advice_sub");
    } else if (mode === "social") {
      createNewSession("regular", "medium", null, "social");
    } else if (mode === "dating") {
      setSelectedModeForSubOptions("dating");
      setModeSelectorStep("dating_sub");
    }
  };

  const handleAdviceSubSelect = (subMode) => {
    if (subMode === "direct") {
      createNewSession("regular", "medium", null, "advice_direct");
    } else if (subMode === "coaching") {
      createNewSession("regular", "medium", null, "advice_coaching");
    }
  };

  const handleDatingSubSelect = (subMode) => {
    if (subMode === "bio") {
      createNewSession("regular", "medium", null, "dating_bio");
    } else if (subMode === "img") {
      createNewSession("regular", "medium", null, "dating_img");
    }
  };

  const handleDifficultySelect = (difficulty) => {
    createNewSession("escalation", difficulty);
  };

  const handleScenarioSelect = (scenarioId) => {
    createNewSession("practice", "medium", scenarioId);
  };

  const handleBackToModeSelection = () => {
    setModeSelectorStep("mode");
    setSelectedModeForSubOptions(null);
  };

  // Coaching Panel handlers
  const openCoachingPanel = (message, messageIndex) => {
    // Find the user message that prompted this persona response
    const userMessage = messages
      .slice(0, messageIndex)
      .reverse()
      .find(m => m.role === "user");
    
    // Create a unique message ID using session + index or use existing message_id
    const messageId = message.message_id || `${currentSessionId}_msg_${messageIndex}`;
    
    // Capture current draft from ChatInput via ref
    const draft = chatInputRef.current?.getValue() || "";
    setCurrentDraft(draft);
    
    // If panel is already open for this message, close and reopen to trigger refresh
    if (coachingPanelOpen && selectedMessageForCoaching?.messageId === messageId) {
      setCoachingPanelOpen(false);
      // Small delay then reopen with fresh draft
      setTimeout(() => {
        setSelectedMessageForCoaching({
          personaMessage: message.content,
          userMessage: userMessage?.content || "",
          messageIndex,
          messageId
        });
        setCoachingPanelOpen(true);
      }, 50);
    } else {
      setSelectedMessageForCoaching({
        personaMessage: message.content,
        userMessage: userMessage?.content || "",
        messageIndex,
        messageId
      });
      setCoachingPanelOpen(true);
    }
  };

  // Track if text was edited in the coaching panel
  const [wasEditedInPanel, setWasEditedInPanel] = useState(false);

  const handleUseCoachResponse = (text, metadata = {}) => {
    // Use the ref to set the value directly in ChatInput
    if (chatInputRef.current) {
      chatInputRef.current.setValue(text);
      // If text was edited in the panel, mark it as co-creative input
      if (metadata.wasEditedInPanel) {
        setWasEditedInPanel(true);
      }
    }
  };

  // Handler for when coaching panel generates tips - updates inline display
  const handleCoachingGenerated = (messageId, annotations) => {
    setMessages(prevMessages => 
      prevMessages.map((msg, idx) => {
        // Find the message by messageId or by matching content
        const msgId = msg.message_id || `${currentSessionId}_msg_${idx}`;
        if (msgId === messageId || msg.content === selectedMessageForCoaching?.personaMessage) {
          return {
            ...msg,
            v2Data: {
              ...msg.v2Data,
              coachAnnotations: annotations
            }
          };
        }
        return msg;
      })
    );
  };

  const handleNewConversationClick = () => {
    // Show mode selector for both persona and advice chats
    setShowModeSelector(true);
    setModeSelectorStep("mode");
    setSelectedModeForSubOptions(null);
  };

  // Store pending media state to apply after images/videos load
  const [pendingMediaState, setPendingMediaState] = useState(null);

  const loadSession = async (sessionId) => {
    try {
      // Check if this is a coaching session (from our sessions list OR by ID pattern)
      const sessionData = sessions.find(s => s.session_id === sessionId);
      const isCoachingSession = sessionData?.is_coaching_session || sessionId.startsWith("coach_session_");
      
      if (isCoachingSession) {
        // Load coaching session - open the coaching panel or navigate to it
        try {
          const coachingResponse = await authAxios.get(`/v2/coaching/session/${sessionId}`);
          const coachingSession = coachingResponse.data;
          
          // Set up state for viewing coaching session
          setCurrentSessionId(sessionId);
          setCurrentSessionContext("advice_coaching");
          
          // Enable V2 coaching for coaching sessions
          setShowCoachAnnotations(true);
          setShowV2TipsInChat(true);
          
          // Convert coaching messages to display format
          const coachingMessages = (coachingSession.messages || []).map((m, idx) => ({
            role: m.role === "assistant" ? "assistant" : "user",
            content: m.content,
            isCoachMessage: true,
            message_id: `${sessionId}_msg_${idx}`
          }));
          
          // Add initial context as first system message
          if (coachingSession.initial_context) {
            coachingMessages.unshift({
              role: "system",
              content: `**Coaching for:** "${coachingSession.initial_context.persona_message?.substring(0, 100)}..."`,
              isContextMessage: true
            });
          }
          
          setMessages(coachingMessages);
          setShowSessionList(false);
          
          toast.success(`Loaded coaching session: ${coachingSession.match_name}`);
          return;
        } catch (coachErr) {
          console.error("Error loading coaching session:", coachErr);
          toast.error("Failed to load coaching session");
          return;
        }
      }
      
      // Regular session loading
      const response = await authAxios.get(`/chat-sessions/${sessionId}`);
      const { session, messages: sessionMessages } = response.data;
      
      setCurrentSessionId(sessionId);
      
      // Update URL with session ID
      updateUrlWithSession(sessionId);
      
      // Restore session mode and settings
      const sessionMode = session.mode || "regular";
      const sessionContext = session.context || "advice";
      setCurrentSessionMode(sessionMode);
      setCurrentSessionContext(sessionContext);
      setPracticeMode(sessionMode === "practice");
      setSelectedScenario(session.scenario_id || "");
      setSelectedDifficulty(session.difficulty || "medium");
      
      // Restore selected match for advice context
      if (session.advice_match_id && !matchId) {
        setSelectedMatchForAdvice(session.advice_match_id);
      }
      
      // Enable V2 coaching for coaching sessions
      if (sessionContext === "advice_coaching") {
        setShowCoachAnnotations(true);
        setShowV2TipsInChat(true);
      }
      
      // Validate session media before rendering
      // Check if images/videos/background are still accessible
      if (matchId) {
        try {
          const mediaValidation = await authAxios.get(`/chat-init/${matchId}/validate-media`, {
            params: { session_id: sessionId }
          });
          
          const { valid_images, valid_videos, background_image, had_missing, rebuilt_indices } = mediaValidation.data;
          
          if (had_missing) {
            console.warn("Some media files were missing, rebuilding from match library");
            toast.info("Some media files were refreshed from match library");
            
            // Update the media lists with validated entries
            if (valid_images && valid_images.length > 0) {
              setMatchImages(valid_images);
            }
            if (valid_videos && valid_videos.length > 0) {
              setMatchVideos(valid_videos);
            }
            
            // Use rebuilt indices if provided
            if (rebuilt_indices) {
              if (rebuilt_indices.left_image_index !== undefined) {
                setLeftImageIndex(rebuilt_indices.left_image_index);
              }
              if (rebuilt_indices.right_image_index !== undefined) {
                setRightImageIndex(rebuilt_indices.right_image_index);
              }
              if (rebuilt_indices.video_index !== undefined) {
                setCurrentVideoIndex(rebuilt_indices.video_index);
              }
            }
          }
        } catch (validationErr) {
          console.error("Media validation failed:", validationErr);
          // Continue loading session even if validation fails
        }
      }
      
      // Store media state to apply once media is loaded
      if (session.left_image_index !== undefined || session.right_image_index !== undefined || session.video_index !== undefined) {
        setPendingMediaState({
          left_image_index: session.left_image_index,
          right_image_index: session.right_image_index,
          video_index: session.video_index
        });
      }
      
      // Restore video index immediately if videos are already loaded
      if (session.video_index !== undefined && session.video_index !== null) {
        setCurrentVideoIndex(session.video_index);
      }
      
      // Restore image indices immediately if images are already loaded
      if (session.left_image_index !== undefined && matchImages.length > 0) {
        setLeftImageIndex(session.left_image_index % matchImages.length);
      }
      if (session.right_image_index !== undefined && matchImages.length > 0) {
        setRightImageIndex(session.right_image_index % matchImages.length);
      }
      
      setMessages(sessionMessages.map(m => {
        // Check stored flags first, then fallback to content pattern detection
        const isSummaryMessage = m.is_summary || 
                                 m.content.startsWith("## CHAT SUMMARY LOG") || 
                                 m.content.includes("**Chat Log:**");
        const isSumMessage = m.is_sum ||
                            m.content.startsWith("📊 **CONVERSATION SUMMARY**") ||
                            m.content.includes("// AI Summary Report");
        const isCoachTip = m.message_type === "coach_tip";
        const isPracticeFeedback = m.message_type === "practice_feedback";
        
        return {
          role: m.role === "system" || isSummaryMessage || isSumMessage ? "system" : m.role,
          content: m.content,
          isSummary: isSummaryMessage,
          isSum: isSumMessage,
          isCoachTip: isCoachTip,
          isPracticeFeedback: isPracticeFeedback,
          score: m.score,
          strengths: m.strengths,
          improvements: m.improvements,
          sumData: null,  // sumData is not persisted, only needed for immediate save action
          // V1 coach annotations (from coach_overlay_agent)
          v1Annotations: m.coach_annotations || [],
          // V2 data - load from stored message if available
          v2Data: (m.interest_level || m.flow || m.coach_annotations_v2) ? {
            interestLevel: m.interest_level,
            flow: m.flow,
            coachAnnotations: m.coach_annotations_v2 || []
          } : null
        };
      }));
      setShowSessionList(false);
      
      // Fetch escalation state and checkpoints for escalation mode sessions
      if (sessionMode === "escalation" && matchId) {
        fetchEscalationState();
        fetchCheckpoints(sessionId);
      }
      
      // Fetch session config to sync V2 state
      await fetchSessionConfig(sessionId);
    } catch (error) {
      console.error("Error loading session:", error);
      toast.error("Failed to load conversation");
    }
  };

  // Apply pending media state when images load
  useEffect(() => {
    if (pendingMediaState && matchImages.length > 0) {
      if (pendingMediaState.left_image_index !== undefined) {
        setLeftImageIndex(pendingMediaState.left_image_index % matchImages.length);
        setPendingMediaState(prev => prev ? { ...prev, left_image_index: undefined } : null);
      }
      if (pendingMediaState.right_image_index !== undefined) {
        setRightImageIndex(pendingMediaState.right_image_index % matchImages.length);
        setPendingMediaState(prev => prev ? { ...prev, right_image_index: undefined } : null);
      }
    }
  }, [matchImages, pendingMediaState]);

  useEffect(() => {
    if (pendingMediaState && matchVideos.length > 0 && pendingMediaState.video_index !== undefined) {
      const idx = pendingMediaState.video_index;
      if (idx >= 0 && idx < matchVideos.length) {
        setCurrentVideoIndex(idx);
      }
      setPendingMediaState(prev => prev ? { ...prev, video_index: undefined } : null);
    }
  }, [matchVideos, pendingMediaState]);

  // Video player handlers
  const handleVideoPlay = () => {
    if (videoRef.current) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => setVideoPlaying(true))
          .catch((error) => {
            // Ignore AbortError - happens when video is removed during play
            if (error.name !== 'AbortError') {
              console.error("Video play error:", error);
            }
          });
      }
    }
  };

  const handleVideoEnded = () => {
    // Move to next video in playlist, loop back to start
    if (matchVideos.length > 1) {
      const nextIndex = (currentVideoIndex + 1) % matchVideos.length;
      handleVideoIndexChange(nextIndex);  // Use save function
      // Video will auto-play due to autoPlay attribute after source change
    } else {
      // Single video - just loop it
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            if (error.name !== 'AbortError') {
              console.error("Video loop error:", error);
            }
          });
        }
      }
    }
  };

  // Auto-play next video when index changes (if already playing)
  useEffect(() => {
    if (videoPlaying && videoRef.current && matchVideos.length > 0) {
      // Support both old (data) and new (src) formats
      const currentVideo = matchVideos[currentVideoIndex];
      const videoSrc = currentVideo?.src || currentVideo?.data || '';
      if (videoSrc) {
        // Only load and play if source actually changed
        const currentSrc = videoRef.current.currentSrc || '';
        if (currentSrc !== videoSrc) {
          videoRef.current.load();
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              // Ignore AbortError - happens when video is removed during play
              if (error.name !== 'AbortError') {
                console.error("Video auto-play error:", error);
              }
            });
          }
        }
      }
    }
  }, [currentVideoIndex, videoPlaying, matchVideos]);

  // Cleanup: pause video when component unmounts or session changes
  useEffect(() => {
    const video = videoRef.current;
    return () => {
      if (video) {
        video.pause();
        setVideoPlaying(false);
      }
    };
  }, [currentSessionId]);

  // Cycle to next image on left side
  const handleLeftImageClick = async () => {
    if (matchImages.length <= 1) return;
    
    const newIndex = (leftImageIndex + 1) % matchImages.length;
    setLeftImageIndex(newIndex);
    
    // Save to session
    if (currentSessionId) {
      try {
        await authAxios.patch(`/chat-sessions/${currentSessionId}`, { 
          left_image_index: newIndex
        });
      } catch (error) {
        console.error("Error saving left image index:", error);
      }
    }
  };

  // Cycle to next image on right side
  const handleRightImageClick = async () => {
    if (matchImages.length <= 1) return;
    
    const newIndex = (rightImageIndex + 1) % matchImages.length;
    setRightImageIndex(newIndex);
    
    // Save to session
    if (currentSessionId) {
      try {
        await authAxios.patch(`/chat-sessions/${currentSessionId}`, { 
          right_image_index: newIndex
        });
      } catch (error) {
        console.error("Error saving right image index:", error);
      }
    }
  };

  // Save video index when user changes video
  const handleVideoIndexChange = async (newIndex) => {
    setCurrentVideoIndex(newIndex);
    
    if (currentSessionId) {
      try {
        await authAxios.patch(`/chat-sessions/${currentSessionId}`, { 
          video_index: newIndex
        });
      } catch (error) {
        console.error("Error saving video index:", error);
      }
    }
  };

  const deleteSession = async (sessionId, e) => {
    e.stopPropagation();
    if (!confirm("Delete this conversation?")) return;
    
    try {
      await authAxios.delete(`/chat-sessions/${sessionId}`);
      setSessions(prev => prev.filter(s => s.session_id !== sessionId));
      if (currentSessionId === sessionId) {
        setCurrentSessionId(null);
        setMessages([]);
        setShowSessionList(true);
        // Reset session-specific states
        setPracticeMode(false);
        setSelectedScenario("");
        setCurrentSessionMode("regular");
      }
      toast.success("Conversation deleted");
    } catch (error) {
      console.error("Error deleting session:", error);
      toast.error("Failed to delete conversation");
    }
  };

  const renameSession = async (sessionId, e) => {
    e.stopPropagation();
    const session = sessions.find(s => s.session_id === sessionId);
    const newTitle = prompt("Rename conversation:", session?.title || "");
    if (!newTitle || newTitle === session?.title) return;
    
    try {
      await authAxios.patch(`/chat-sessions/${sessionId}`, { title: newTitle });
      setSessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, title: newTitle } : s
      ));
      toast.success("Conversation renamed");
    } catch (error) {
      console.error("Error renaming session:", error);
      toast.error("Failed to rename conversation");
    }
  };

  const toggleFavourite = async (sessionId, e) => {
    e.stopPropagation();
    const session = sessions.find(s => s.session_id === sessionId);
    const newFavourite = !session?.is_favourite;
    
    try {
      await authAxios.patch(`/chat-sessions/${sessionId}`, { is_favourite: newFavourite });
      setSessions(prev => prev.map(s => 
        s.session_id === sessionId ? { ...s, is_favourite: newFavourite } : s
      ));
      toast.success(newFavourite ? "Added to favourites" : "Removed from favourites");
    } catch (error) {
      console.error("Error toggling favourite:", error);
      toast.error("Failed to update favourite");
    }
  };

  const fetchChatState = async () => {
    try {
      const response = await authAxios.get(`/persona-chat-state/${matchId}`);
      setChatState(response.data);
    } catch (error) {
      console.error("Error fetching chat state:", error);
    }
  };

  // Memoized handler for ChatInput component
  const handleChatInputSend = useCallback(async (messageText, coCreativeMetadata = {}) => {
    if (!messageText.trim()) return;
    
    // Store co-creative metadata for escalation processing
    // If text was edited in the AgentEros panel, treat it as co-creative input
    const metadata = {
      userHadDraft: coCreativeMetadata.userHadDraft || wasEditedInPanel || false,
      usedSuggestionVerbatim: wasEditedInPanel ? false : (coCreativeMetadata.usedSuggestionVerbatim || false),
      lastUserMessage: messageText
    };
    
    // Reset the wasEditedInPanel flag after use
    setWasEditedInPanel(false);
    
    // Auto-create session if none selected
    if (!currentSessionId) {
      try {
        const response = await authAxios.post("/chat-sessions", {
          match_id: matchId || null,
          context: matchId ? "persona_chat" : "advice"
        });
        
        const newSession = response.data;
        setSessions(prev => [newSession, ...prev]);
        setCurrentSessionId(newSession.session_id);
        setShowSessionList(false);
        
        // Update URL with new session ID
        updateUrlWithSession(newSession.session_id);
        
        // Now send the message with the new session
        await sendMessageRef.current(messageText, newSession.session_id, metadata);
        return;
      } catch (error) {
        console.error("Error creating session:", error);
        toast.error("Failed to start conversation");
        return;
      }
    }
    
    await sendMessageRef.current(messageText, currentSessionId, metadata);
  }, [currentSessionId, matchId, wasEditedInPanel]);

  const sendMessage = async (userMsg, sessionId, coCreativeMetadata = {}) => {
    // Reset textarea height
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    
    // Check if it's a command
    const isCommand = userMsg.startsWith("/");
    
    if (!isCommand) {
      setMessages(prev => [...prev, { role: "user", content: userMsg }]);
    }
    
    setLoading(true);
    setLastPracticeFeedback(null);
    
    try {
      let response;
      
      // Commands always go to V1 API (V2 doesn't handle commands)
      if (isCommand) {
        response = await authAxios.post("/ai/chat", {
          message: userMsg,
          match_id: matchId || null,
          context: matchId ? "persona_chat" : "advice",
          session_id: sessionId,
          max_words: matchId ? maxWords : null
        });
        
        // Handle command response
        if (response.data.is_command) {
          const systemMsg = {
            role: "system",
            content: response.data.response
          };
          setMessages(prev => [...prev, systemMsg]);
          
          // Handle /rollback command - update credits
          if (response.data.rollback_credits !== undefined) {
            setRollbackCredits(response.data.rollback_credits);
          }
          
          // Handle special command actions
          if (response.data.clear_messages) {
            setTimeout(() => {
              setMessages([systemMsg]);
            }, 100);
          }
        } else {
          setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
        }
      }
      // Use V2 API for persona chat when enabled (non-commands only)
      else if (matchId && useV2) {
        // Only include coach if either V1 or V2 tips are enabled (saves tokens when both off)
        const shouldIncludeCoach = showV1TipsInChat || showV2TipsInChat;
        
        const params = new URLSearchParams({
          persona_id: matchId,
          message: userMsg,
          include_coach: shouldIncludeCoach.toString(),  // Derived from V1/V2 tip toggles
          practice_mode: practiceMode.toString(),
        });
        if (selectedScenario) {
          params.append("scenario_id", selectedScenario);
        }
        if (sessionId) {
          params.append("session_id", sessionId);
        }
        if (maxWords && maxWords > 0) {
          params.append("max_words", maxWords.toString());
        }
        // Add model selection only if explicitly chosen (not "Admin Default")
        if (selectedModel && selectedModel.provider && selectedModel.name) {
          params.append("model_provider", selectedModel.provider);
          params.append("model_name", selectedModel.name);
        }
        
        response = await authAxios.post(`/v2/persona/chat?${params.toString()}`);
        
        // Process V2 response
        const personaResponse = response.data.persona_response?.message || response.data.error || "No response";
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: personaResponse,
          // V1 coach annotations (from coach_overlay_agent)
          v1Annotations: response.data.coach_annotations || [],
          // V2 data
          v2Data: {
            interestLevel: response.data.persona_response?.interest_level,
            flow: response.data.persona_response?.conversation_flow,
            coachAnnotations: response.data.coach_annotations_v2 || [],
            signals: response.data.relationship_signals
          }
        }]);
        
        // Store practice feedback if in practice mode
        if (practiceMode && response.data.practice_feedback) {
          setLastPracticeFeedback(response.data.practice_feedback);
        }
        
        // Update drift info for visual display
        if (response.data.drift_info) {
          setDriftInfo(response.data.drift_info);
        }
        
        // Refresh sessions to update title and message count
        fetchSessions();
        
      } else if (!matchId && v2SystemEnabled) {
        // V2 Coaching API for advice chat when V2 toggle is enabled
        response = await authAxios.post("/v2/chat", {
          text: userMsg,
          persona_id: selectedMatchForAdvice || null,
          session_id: sessionId,
          debug_mode: false
        });
        
        // V2 response format
        const v2Response = response.data.text || response.data.response || "No response";
        setMessages(prev => [...prev, { 
          role: "assistant", 
          content: v2Response,
          v2Data: {
            traceId: response.data.trace_id,
            productMode: response.data.product_mode,
            subMode: response.data.sub_mode,
            signals: response.data.signals_summary
          }
        }]);
        
      } else {
        // V1 API (default for advice chat when V2 disabled)
        const v1Payload = {
          message: userMsg,
          match_id: matchId || selectedMatchForAdvice || null,  // Use selected match for advice context
          context: matchId ? "persona_chat" : "advice",
          session_id: sessionId,
          max_words: matchId ? maxWords : adviceResponseLength,  // Use advice settings for non-match chat
          include_user_data: !matchId && adviceUseUserData,  // Include user data silo if enabled
        };
        // Only add model params if explicitly selected (not "Admin Default")
        if (selectedModel && selectedModel.provider && selectedModel.name) {
          v1Payload.model_provider = selectedModel.provider;
          v1Payload.model_name = selectedModel.name;
        }
        response = await authAxios.post("/ai/chat", v1Payload);
        
        // Add response (for commands, show as system message)
        if (response.data.is_command) {
          setMessages(prev => [...prev, { 
            role: "system", 
            content: response.data.response,
            isSummary: response.data.is_summary || false,
            isSum: response.data.is_sum || false,
            sumData: response.data.sum_data || null
          }]);
          
          // Handle /clear command
          if (response.data.clear_messages) {
            setTimeout(() => {
              setMessages([{ role: "system", content: response.data.response }]);
            }, 100);
          }
          
          // Handle /rollback command - update credits
          if (response.data.rollback_credits !== undefined) {
            setRollbackCredits(response.data.rollback_credits);
          }
        } else {
          setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
        }
        
        // Update chat state if persona chat
        if (matchId && response.data.chat_state) {
          // If level changed, clear previous state (variables reset)
          if (response.data.chat_state.level_changed) {
            setPreviousChatState(null); // Clear to reset indicators
            const newLevel = response.data.chat_state.relationship_level;
            toast.success(`Connection progressed to: ${LEVEL_NAMES[newLevel]}!`);
          } else {
            setPreviousChatState(chatState); // Save previous for delta calculation
          }
          
          setChatState(response.data.chat_state);
        }
      }
      
      // Hide loading bubble before escalation processing (response already received)
      setLoading(false);
      
      // Process escalation game after message (for persona chat)
      if (matchId && currentSessionMode === "escalation") {
        await processEscalationGame(sessionId, coCreativeMetadata);
        // Refresh achievements and alternate display
        await fetchAchievementsForHeader();
        setAchievementDisplayIndex(prev => prev + 1);
      }
      
      // For non-escalation persona chat, refresh match to get updated persona_connection
      if (matchId && currentSessionMode !== "escalation") {
        fetchMatch();
      }
    } catch (error) {
      console.error("Send error:", error);
      toast.error("Failed to send message");
    } finally {
      setLoading(false);
      // Auto-focus input after response
      setTimeout(() => inputRef.current?.focus(), 100);
      // Reset co-creative state in ChatInput
      if (chatInputRef.current?.resetCoCreativeState) {
        chatInputRef.current.resetCoCreativeState();
      }
    }
  };

  // Keep sendMessageRef updated with the latest sendMessage function
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  });

  // Handle saving summary to memory
  const handleSaveToMemory = async (sumData) => {
    if (!sumData || !sumData.match_id) {
      toast.error("Cannot save - missing data");
      return;
    }
    
    // Get current session title or generate default
    const currentSession = sessions.find(s => s.session_id === currentSessionId);
    const sessionTitle = currentSession?.title || "Chat Summary";
    const now = new Date();
    const dateStr = now.toLocaleDateString();
    
    // Auto-generate title from session name and date
    const title = `${sessionTitle} - ${dateStr}`;
    
    try {
      await authAxios.post(`/familiarity/${sumData.match_id}/summaries`, {
        title: title,
        content: sumData.summary_content,
        include_in_memory: true,
        messages_analyzed: sumData.messages_analyzed,
        relationship_level: sumData.relationship_level,
        word_limit: sumData.word_limit
      });
      
      toast.success("Summary saved to memory!");
    } catch (error) {
      console.error("Save to memory error:", error);
      toast.error("Failed to save summary");
    }
  };

  const handleReset = async () => {
    if (!confirm("Reset chat? This will clear all variables and chat history, keeping your current relationship level (minimum 1).")) return;
    
    try {
      const response = await authAxios.post(`/persona-chat-state/${matchId}/reset`, {});
      
      // Add system message to chat
      setMessages([{ role: "system", content: "🔄 CHAT RESET - Variables cleared. Fresh start!" }]);
      
      toast.success("Chat reset - fresh start!");
      
      fetchChatState();
    } catch (error) {
      toast.error("Failed to reset chat");
    }
  };

  const chatTitle = currentSessionContext === "advice_coaching" && matchId && match
    ? `Coaching Session with ${match.name}`
    : matchId && match 
      ? `Chat with ${match.name}'s Persona` 
      : currentSessionContext === "advice_coaching"
        ? "Coaching Session"
        : "Chat with Agent Eros";

  const chatSubtitle = currentSessionContext === "advice_coaching" && matchId && match
    ? "Get personalized dating advice for this connection"
    : matchId && match
      ? "Practice conversations with their AI persona"
      : currentSessionContext === "advice_coaching"
        ? "Get personalized dating coaching"
        : "Get divine relationship advice";

  const getVariableColor = (value) => {
    if (value > 30) return "bg-green-500";
    if (value > 0) return "bg-yellow-500";
    if (value > -30) return "bg-orange-500";
    return "bg-red-500";
  };

  const getValueBarWidth = (value) => {
    // Map -100 to 0%, 0 to 50%, +100 to 100%
    const percentage = ((value + 100) / 200) * 100;
    return Math.max(0, Math.min(100, percentage));
  };

  const getDelta = (variableName) => {
    if (!previousChatState || !chatState) return null;
    const current = chatState[variableName] || 0;
    const previous = previousChatState[variableName] || 0;
    const delta = current - previous;
    if (delta === 0) return null;
    return delta;
  };

  const renderVariableWithDelta = (name, value) => {
    const delta = getDelta(name);
    
    return (
      <div>
        <div className="flex items-center justify-between mb-0.5 h-5">
          <span className="text-xs text-white/60 capitalize">{name}</span>
          <div className="flex items-center gap-1 min-w-[70px] justify-end h-5">
            {delta !== null && delta !== 0 ? (
              <>
                <span className={`text-xs font-bold w-8 text-right ${delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {delta > 0 ? '+' : ''}{delta}
                </span>
                <span className={`w-3 text-center ${delta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {delta > 0 ? '↑' : '↓'}
                </span>
              </>
            ) : (
              <span className="text-xs text-white/30 w-8 text-right">—</span>
            )}
            <span className="text-xs font-semibold text-white w-6 text-right">{value}</span>
          </div>
        </div>
        <div className="w-full bg-white/10 rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all ${getVariableColor(value)}`}
            style={{ width: `${getValueBarWidth(value)}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-divine-black flex flex-col">
      <Toaster position="top-center" richColors />
      
      {/* Header - Using extracted component */}
      <ChatHeader
        matchId={matchId}
        match={match}
        showSessionList={showSessionList}
        onBack={() => {
          if (!showSessionList) {
            // In chat view - go back to session list
            setShowSessionList(true);
            setCurrentSessionId(null);
            setMessages([]);
            // Clear escalation mode state when leaving chat
            setCurrentSessionMode("regular");
            // Update URL to remove session ID
            if (matchId) {
              navigate(`/chat/${matchId}`, { replace: true });
            } else {
              navigate("/chat", { replace: true });
            }
          } else {
            // In session list - go back to relationships or dashboard
            navigate(matchId ? "/relationships" : "/dashboard");
          }
        }}
        chatTitle={chatTitle}
        chatSubtitle={chatSubtitle}
        sessionTokens={sessionTokens}
        sessionCost={sessionCost}
        coachingTokens={coachingTokens}
        coachingCost={coachingCost}
        currentSessionId={currentSessionId}
        currentSessionMode={currentSessionMode}
        // V2 System toggle
        v2SystemEnabled={v2SystemEnabled}
        setV2SystemEnabled={setV2SystemEnabled}
        onV2SystemToggle={handleV2SystemToggle}
        // Advice chat controls
        adviceUseUserData={adviceUseUserData}
        setAdviceUseUserData={setAdviceUseUserData}
        selectedMatchForAdvice={selectedMatchForAdvice}
        setSelectedMatchForAdvice={setSelectedMatchForAdvice}
        allMatches={allMatches}
        adviceResponseLength={adviceResponseLength}
        setAdviceResponseLength={setAdviceResponseLength}
        // Persona chat controls
        maxWords={maxWords}
        setMaxWords={setMaxWords}
        showCoachAnnotations={showCoachAnnotations}
        setShowCoachAnnotations={setShowCoachAnnotations}
        // V2 Tips toggle (coach_v2)
        showV2TipsInChat={showV2TipsInChat}
        setShowV2TipsInChat={setShowV2TipsInChat}
        // V1 Tips toggle (coach_overlay_agent)
        showV1TipsInChat={showV1TipsInChat}
        setShowV1TipsInChat={setShowV1TipsInChat}
        useV2={useV2}
        // Model selection
        selectedModel={selectedModel}
        setSelectedModel={setSelectedModel}
        availableModels={AVAILABLE_MODELS}
        // Objectives panel
        onOpenObjectives={() => setShowObjectiveSlideout(true)}
      />

      {/* Scenario Help Text (Practice Mode Only) */}
      {matchId && useV2 && practiceMode && selectedScenario && (
        <div className="border-b border-white/10 bg-cyan-500/5 backdrop-blur-xl">
          <div className="max-w-4xl mx-auto px-6 py-2">
            <p className="text-cyan-400 text-xs">
              <strong>Practicing:</strong>{" "}
              {selectedScenarioDesc}
            </p>
          </div>
        </div>
      )}

      {/* Gamification Stats (Escalation Mode Sessions Only) - Using extracted component */}
      {matchId && currentSessionMode === "escalation" && escalationState && (
        <>
          <EscalationProgressBar
            escalationState={escalationState}
            driftInfo={driftInfo}
            selectedDifficulty={selectedDifficulty}
            currentAchievementDisplay={currentAchievementDisplay}
            onShowLevelModal={() => setShowLevelModal(true)}
            onShowObjectiveSlideout={() => setShowObjectiveSlideout(true)}
            compactProgressMode={compactProgressMode}
            setCompactProgressMode={setCompactProgressMode}
          />
          
          {/* Achievements Panel (includes Level Objective) - flush with progress bar, overlays chat */}
          {!compactProgressMode && (
            <div className="border-b border-white/10 bg-divine-charcoal/95 backdrop-blur-xl sticky top-[125px] z-40 ml-20">
              <div className="max-w-4xl mx-auto px-6 py-1">
                <AchievementPanel 
                  relationshipId={matchId} 
                  escalationState={escalationState} 
                  onOpenSlideout={() => setShowObjectiveSlideout(true)}
                />
              </div>
            </div>
          )}
        </>
      )}

      {/* Display-Only Connection Level Circle (Non-Escalation Mode) - Using extracted component */}
      {matchId && currentSessionMode !== "escalation" && match?.persona_connection && !showSessionList && (
        <ConnectionLevelCircle
          level={match.persona_connection}
          driftInfo={driftInfo}
          onClick={() => setShowLevelModal(true)}
        />
      )}

      {/* Connection Level Info Modal - Using extracted component */}
      <ConnectionLevelModal
        isOpen={showLevelModal}
        onClose={() => setShowLevelModal(false)}
        currentLevel={
          currentSessionMode === "escalation" && escalationState 
            ? escalationState.level 
            : match?.persona_connection || 0
        }
        driftInfo={driftInfo}
        levelScrollRef={levelScrollRef}
        isEscalationMode={currentSessionMode === "escalation"}
        escalationState={escalationState}
        matchPersonaConnection={match?.persona_connection}
      />

      {/* Blocked Alert - V2 Escalation: XP reaches -50 at Level 1 */}
      {matchId && currentSessionMode === "escalation" && escalationState && escalationState.level === 1 && escalationState.xp <= -50 && (
        <div className="border-b border-red-500 bg-red-500/20">
          <div className="max-w-4xl mx-auto px-6 py-4 text-center">
            <h2 className="text-red-400 font-bold text-2xl mb-2">🚫 YOU HAVE BEEN BLOCKED 🚫</h2>
            <p className="text-white/80 text-sm">
              The connection has been lost. Your XP dropped too low at Level 1.
            </p>
            <p className="text-white/50 text-xs mt-2">
              Start a new conversation to try again.
            </p>
          </div>
        </div>
      )}

      {/* Session Selection Panel - Using extracted components */}
      {showSessionList && !currentSessionId && (
        <div className="flex-1 overflow-y-auto">
          <SessionList
            sessions={sessions}
            loadingSessions={loadingSessions}
            matchId={matchId}
            match={match}
            scenarioNameMap={scenarioNameMap}
            onLoadSession={loadSession}
            onNewConversation={handleNewConversationClick}
            onRenameSession={(sessionId, e) => { e.stopPropagation(); renameSession(sessionId, e); }}
            onDeleteSession={(sessionId, e) => { e.stopPropagation(); deleteSession(sessionId, e); }}
            onToggleFavourite={(sessionId, e) => { e.stopPropagation(); toggleFavourite(sessionId, e); }}
            // Mode selector props - expanding panel
            showModeSelector={showModeSelector}
            modeSelectorStep={modeSelectorStep}
            sortedPracticeScenarios={sortedPracticeScenarios}
            onModeSelect={handleModeSelect}
            onDifficultySelect={handleDifficultySelect}
            onScenarioSelect={handleScenarioSelect}
            onBackToModeSelection={handleBackToModeSelection}
            onCloseModeSelector={() => {
              setShowModeSelector(false);
              setModeSelectorStep("mode");
              setSelectedModeForSubOptions(null);
            }}
            // Advice mode handlers (for Eros Chat without matchId)
            onAdviceModeSelect={handleAdviceModeSelect}
            onAdviceSubSelect={handleAdviceSubSelect}
            onDatingSubSelect={handleDatingSubSelect}
            // Connection level for Regular mode description
            connectionLevel={match?.persona_connection}
            connectionLevelName={match?.persona_connection ? STAGE_LABELS[match.persona_connection] : null}
            // User persona name for Live mode
            userPersonaName={user?.name}
          />
        </div>
      )}

      {/* Chat Messages - Only show when session selected */}
      {(!showSessionList || currentSessionId) && (
      <div 
        ref={chatContainerRef} 
        className="flex-1 overflow-y-auto relative w-full max-w-full" 
        data-testid="chat-messages"
      >
        {/* Persona Avatar Background - Desktop only (mobile uses special layered approach below) */}
        {!isMobile && matchId && (
          <div 
            className="fixed z-0 pointer-events-none flex items-center justify-center"
            style={{
              height: '60vh',
              width: '100%',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)'
            }}
          >
            {(match?.picture_url || match?.picture) ? (
              <div 
                className="relative overflow-hidden h-full rounded-xl border-2 border-white/10 shadow-2xl" 
                style={{ aspectRatio: '3/4' }}
              >
                <img 
                  src={match.picture_url || match.picture} 
                  alt={match?.name}
                  className="w-full h-full object-cover opacity-50"
                />
                <div className="absolute inset-0 bg-divine-black/40" />
              </div>
            ) : (
              <div 
                className="w-full h-full"
                style={{
                  background: `radial-gradient(ellipse at center, rgba(218, 165, 32, 0.15) 0%, transparent 70%)`
                }}
              />
            )}
          </div>
        )}
        
        {/* Media Sidebar - Using extracted component */}
        <MediaSidebar
          matchId={matchId}
          match={match}
          matchImages={matchImages}
          leftImageIndex={leftImageIndex}
          rightImageIndex={rightImageIndex}
          matchVideos={matchVideos}
          currentVideoIndex={currentVideoIndex}
          onVideoIndexChange={handleVideoIndexChange}
          onLeftImageClick={handleLeftImageClick}
          onRightImageClick={handleRightImageClick}
          onVideoPlay={handleVideoPlay}
          onVideoEnded={handleVideoEnded}
          videoPlaying={videoPlaying}
          videoRef={videoRef}
          selectedMatchForAdvice={selectedMatchForAdvice}
          selectedMatchData={selectedMatchData}
          adviceUseUserData={adviceUseUserData}
          user={user}
        />
        
        {/* Mobile: Layered z-index for fade effect */}
        {/* z-30: Chat content (scrollable) */}
        {/* z-40: Fade overlay (hides chat in top 50%) */}
        {/* z-50: Background image (shows through overlay) */}
        
        {/* Fade Overlay - dark gradient covering top 50% */}
        {isMobile && matchId && (
          <div 
            className="fixed pointer-events-none"
            style={{
              zIndex: 35,
              top: '60px',
              left: 0,
              right: 0,
              height: 'calc((100vh - 140px) * 0.5)',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.92) 75%, rgba(0,0,0,0.5) 90%, transparent 100%)'
            }}
          />
        )}
        
        {/* Background Image - positioned above overlay so it shows through */}
        {isMobile && matchId && (match?.picture_url || match?.picture) && (
          <div 
            className="fixed pointer-events-none"
            style={{
              zIndex: 36,
              top: '60px',
              left: 0,
              right: 0,
              height: 'calc((100vh - 140px) * 0.5)',
              overflow: 'hidden'
            }}
          >
            <img 
              src={match.picture_url || match.picture} 
              alt={match?.name}
              className="w-full h-full object-cover object-top opacity-90"
              style={{
                maskImage: 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 0%, black 70%, transparent 100%)'
              }}
            />
          </div>
        )}
        
        <div 
          className={`${isMobile ? 'w-full px-3' : 'max-w-4xl mx-auto px-6'} py-6 pb-24 space-y-6 relative z-30`}
        >
          {/* Mobile overscroll space - allows scrolling past first message (60vh) */}
          {isMobile && messages.length > 0 && !visibleMessages.hasMore && (
            <div style={{ height: '60vh' }} className="flex-shrink-0" />
          )}
          
          {messages.length === 0 && (
            <div className="text-center py-16">
              <Heart className="w-12 h-12 text-white/20 mx-auto mb-4" />
              <p className="text-white/40">
                {matchId ? `Start chatting with ${match?.name || "Persona"}` : "Seek Eros's Wisdom"}
              </p>
            </div>
          )}

          {/* Auto-loading indicator - shown when loading more messages */}
          {isLoadingMore && (
            <div className="text-center py-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm bg-divine-gold/10 text-divine-gold">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                <span>Loading older messages...</span>
              </div>
            </div>
          )}
          
          {/* Load trigger element - observed by Intersection Observer */}
          {visibleMessages.hasMore && !isLoadingMore && (
            <div ref={loadTriggerRef} className="text-center py-2">
              <span className="text-white/30 text-xs">
                ↑ Scroll up to load {visibleMessages.startIndex} more messages
              </span>
            </div>
          )}

          {visibleMessages.messages.map((message, idx) => {
            // Calculate actual index in full messages array
            const index = visibleMessages.startIndex + idx;
            return (
            <div
              key={index}
              className={`flex gap-4 ${message.role === "user" ? "justify-end" : message.role === "system" ? "justify-center" : "justify-start"}`}
            >
              {/* Eros Tip Display */}
              {message.isCoachTip ? (
                <ErosTipBubble content={message.content} />
              ) : message.isPracticeFeedback ? (
                <PracticeFeedbackBubble message={message} />
              ) : message.role === "system" && (message.isSummary || message.isSum) ? (
              /* System Log Style for /sum and /summary commands */
                <div className="w-full">
                  <div className="bg-black/80 border border-white/10 rounded-lg p-4 font-mono text-sm">
                    <div className="text-green-400/80 text-xs mb-2 uppercase tracking-wider">
                      {message.isSum ? "// AI Summary Report" : "// Chat Summary Log"}
                    </div>
                    <pre className="text-white/80 whitespace-pre-wrap break-words leading-relaxed">
                      {message.content.replace(/\*\*/g, '').replace(/━/g, '-')}
                    </pre>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2 mt-4 pt-3 border-t border-white/10">
                      {message.isSum && message.sumData && (
                        <Button
                          size="sm"
                          onClick={() => handleSaveToMemory(message.sumData)}
                          className="bg-green-600 hover:bg-green-500 text-white font-mono text-xs"
                        >
                          <Save className="w-3 h-3 mr-2" />
                          SAVE TO MEMORY
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(message.content.replace(/\*\*/g, '').replace(/━/g, '-'));
                          toast.success("Copied to clipboard!");
                        }}
                        className="text-white/60 hover:text-white hover:bg-white/10 font-mono text-xs"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-3 h-3 mr-1" />
                        {message.isSum ? "" : "COPY"}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <>
              {/* Assistant Avatar - Left side (only on desktop) */}
              {message.role === "assistant" && (
                <div className="hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 overflow-hidden bg-divine-gold/20">
                  {currentSessionContext === "advice_coaching" || currentSessionContext === "advice_direct" || currentSessionContext === "advice" ? (
                    <div className="relative">
                      <div className="absolute inset-0 bg-divine-gold/50 blur-sm rounded-full" />
                      <Brain className="w-5 h-5 text-divine-gold relative" />
                    </div>
                  ) : matchId && (match?.thumbnail_url || match?.picture_url || match?.picture) ? (
                    <img src={match.thumbnail_url || match.picture_url || match.picture} alt={match.name} className="w-full h-full object-cover" />
                  ) : matchId && match ? (
                    <span className="text-divine-gold font-cinzel text-sm">{match.name[0]}</span>
                  ) : (
                    <Heart className="w-5 h-5 text-divine-red fill-divine-red" />
                  )}
                </div>
              )}
              
              <div className={`${message.role === "system" ? "max-w-[90%]" : "max-w-full sm:max-w-[70%]"}`}>
                <div className={`inline-block rounded-2xl px-5 py-3 relative ${
                  message.role === "user"
                    ? "bg-emerald-500/40 border border-emerald-500/30 text-white"
                    : message.role === "system"
                      ? "bg-white/10 border border-white/10 text-white/70 text-sm"
                      : "glass-gold text-white"
                }`}>
                      <MessageContent
                        content={message.content}
                        matchPersonaId={matchId || selectedMatchForAdvice}
                        sessionId={currentSessionId}
                      />
                  
                  {/* Coach Icon + AI Disclosure Badge - Inside bubble, bottom */}
                  {/* Hide in coaching sessions (advice_coaching) since it's already a coaching context */}
                  {matchId && message.role === "assistant" && currentSessionContext !== "advice_coaching" && (
                    <div className="flex items-center justify-between mt-2 -mb-1 -mr-2">
                      {/* Eros Icon with Signal Indicator - Only show when Eros Tips toggle is ON */}
                      {showCoachAnnotations && (
                        <button
                          onClick={() => openCoachingPanel(message, index)}
                          className="group relative flex items-center gap-1 px-2 py-0.5 rounded-full transition-all bg-divine-gold/10 hover:bg-divine-gold/20 text-divine-gold"
                          title="Open Agent Eros advice for this message"
                        >
                          <span className="text-sm">🏹</span>
                          <span className="text-[10px] font-medium opacity-70 group-hover:opacity-100">EROS</span>
                          {/* Signal Indicator - Based on Interest Level and Flow */}
                          {(() => {
                            const interestLevel = message.v2Data?.interestLevel;
                            const flow = message.v2Data?.flow;
                            
                            // Flow-based animation classes
                            // - advancing: pulse (gentle glow)
                            // - stalling: blink (attention needed)
                            // - ending: dim (faded out)
                            const flowClass = 
                              flow === "advancing" ? "animate-pulse" :
                              flow === "stalling" ? "animate-blink" :
                              flow === "ending" ? "opacity-30" : "";
                            
                            const flowLabel = flow ? ` (${flow})` : "";
                            
                            // Color based on interest level (same thresholds as Interest display)
                            if (interestLevel !== undefined) {
                              // ≥ 70% = Green
                              if (interestLevel >= 70) {
                                return (
                                  <span className={`ml-1 ${flowClass}`} title={`Interest: ${interestLevel}%${flowLabel}`}>
                                    🟢
                                  </span>
                                );
                              }
                              // 40-69% = Yellow
                              if (interestLevel >= 40) {
                                return (
                                  <span className={`ml-1 ${flowClass}`} title={`Interest: ${interestLevel}%${flowLabel}`}>
                                    🟡
                                  </span>
                                );
                              }
                              // < 40% = Red
                              return (
                                <span className={`ml-1 ${flowClass}`} title={`Interest: ${interestLevel}%${flowLabel}`}>
                                  🔴
                                </span>
                              );
                            }
                            
                            // Fallback: no interest data - show yellow
                            return (
                              <span className={`ml-1 opacity-50 ${flowClass}`} title="Click for coaching">
                                🟡
                              </span>
                            );
                          })()}
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-0 mb-1 px-2 py-1 bg-divine-charcoal/95 border border-white/10 rounded text-[10px] text-white/70 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                            Open Agent Eros advice for this message
                          </div>
                        </button>
                      )}
                      {/* AI Disclosure Badge */}
                      <div className={`group relative inline-block ${!showCoachAnnotations ? 'ml-auto' : ''}`}>
                        <span className="text-white/30 text-[10px] cursor-default hover:text-white/50 transition-colors">
                          🏹 AgentEros.ai
                        </span>
                        <div className="absolute bottom-full right-0 mb-1 px-2 py-1 bg-divine-charcoal/95 border border-white/10 rounded text-[10px] text-white/70 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                          ✨ AI-generated text lovingly crafted by AgentEros.ai
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* V2 Metadata - Interest Level & Flow + Rollback */}
                {message.v2Data && message.role === "assistant" && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <div className="flex flex-wrap items-center gap-2">
                      {message.v2Data.interestLevel !== undefined && (
                        <span className={`px-2 py-0.5 rounded-full ${
                          message.v2Data.interestLevel >= 70 ? "bg-green-500/20 text-green-400" :
                          message.v2Data.interestLevel >= 40 ? "bg-yellow-500/20 text-yellow-400" :
                          "bg-red-500/20 text-red-400"
                        }`}>
                          Interest: {message.v2Data.interestLevel}%
                        </span>
                      )}
                      {message.v2Data.flow && (
                        <span className={`px-2 py-0.5 rounded-full ${
                          message.v2Data.flow === "advancing" ? "bg-green-500/20 text-green-400" :
                          message.v2Data.flow === "stalling" ? "bg-orange-500/20 text-orange-400" :
                          message.v2Data.flow === "ending" ? "bg-red-500/20 text-red-400" :
                          "bg-white/10 text-white/60"
                        }`}>
                          {message.v2Data.flow}
                        </span>
                      )}
                    </div>
                    {/* Rollback Button - Inline with Interest % (not on last assistant message) */}
                    {currentSessionMode === "escalation" && index !== lastAssistantIndex && (
                      <RollbackButton
                        index={index}
                        checkpoints={checkpoints}
                        rollbackCredits={rollbackCredits}
                        openRollbackModal={openRollbackModal}
                      />
                    )}
                  </div>
                )}
                
                {/* Rollback Button - For non-V2 escalation mode messages (not on last assistant message) */}
                {!message.v2Data && message.role === "assistant" && currentSessionMode === "escalation" && index !== lastAssistantIndex && (
                  <div className="mt-2 flex justify-end">
                    <RollbackButton
                      index={index}
                      checkpoints={checkpoints}
                      rollbackCredits={rollbackCredits}
                      openRollbackModal={openRollbackModal}
                    />
                  </div>
                )}
                
                {/* V1 Coach Annotations - Show if V1 Tips toggle is ON and data exists */}
                <CoachAnnotations 
                  annotations={message.v1Annotations} 
                  showAnnotations={showV1TipsInChat && !!message.v1Annotations?.length} 
                  source="v1"
                />
                
                {/* V2 Coach Annotations - Show if V2 Tips toggle is ON and data exists */}
                <CoachAnnotations 
                  annotations={message.v2Data?.coachAnnotations} 
                  showAnnotations={showV2TipsInChat && !!message.v2Data?.coachAnnotations?.length} 
                  source="v2"
                />
              </div>
              
              {/* User Avatar - Right side (only on desktop) */}
              {message.role === "user" && (
                <div className={`hidden sm:flex w-10 h-10 rounded-full items-center justify-center flex-shrink-0 overflow-hidden bg-emerald-500/20 border border-emerald-500/30`}>
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <User className="w-5 h-5 text-emerald-400" />
                  )}
                </div>
              )}
              </>
              )}
            </div>
          );
          })}

          {/* Practice Feedback Panel */}
          <PracticeFeedbackPanel 
            feedback={lastPracticeFeedback} 
            practiceMode={practiceMode} 
          />

          {loading && (
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-divine-red/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-divine-red animate-pulse" />
              </div>
              <div className="glass-gold rounded-2xl px-5 py-3">
                <div className="flex gap-2">
                  <div className="w-2 h-2 bg-divine-gold rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-divine-gold rounded-full animate-bounce" style={{ animationDelay: "0.1s" }} />
                  <div className="w-2 h-2 bg-divine-gold rounded-full animate-bounce" style={{ animationDelay: "0.2s" }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      )}

      {/* Input Area - Sticky at bottom */}
      <div className="sticky bottom-0 border-t border-white/10 bg-divine-black-light/95 backdrop-blur-xl z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          {/* Check if blocked (V2: XP <= -50 at Level 1) */}
          {matchId && currentSessionMode === "escalation" && escalationState && escalationState.level === 1 && escalationState.xp <= -50 ? (
            <div className="text-center py-4">
              <p className="text-white/60 mb-3">
                You have been blocked. No further interaction possible.
              </p>
              <Button
                onClick={() => {
                  setCurrentSessionId(null);
                  setShowSessionList(true);
                  setCurrentSessionMode("regular");
                }}
                className="bg-divine-gold hover:bg-divine-gold-light text-black"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Start New Conversation
              </Button>
            </div>
          ) : (
            <ChatInput
              ref={chatInputRef}
              onSend={handleChatInputSend}
              loading={loading}
              placeholder={getPlaceholderText()}
            />
          )}
        </div>
      </div>
      
      {/* Rollback Modal */}
      <RollbackModal
        isOpen={rollbackModalOpen}
        onClose={() => {
          setRollbackModalOpen(false);
          setSelectedCheckpoint(null);
        }}
        onConfirm={executeRollback}
        checkpoint={selectedCheckpoint}
        currentState={{
          level: escalationState?.level,
          xp: escalationState?.xp,
          messageCount: messages.length
        }}
        rollbackCredits={rollbackCredits}
        loading={rollbackLoading}
      />
      
      {/* Coaching Panel */}
      <CoachingPanel
        isOpen={coachingPanelOpen}
        onClose={() => {
          setCoachingPanelOpen(false);
          setSelectedMessageForCoaching(null);
        }}
        personaMessage={selectedMessageForCoaching?.personaMessage}
        userMessage={selectedMessageForCoaching?.userMessage}
        messageId={selectedMessageForCoaching?.messageId}
        personaId={matchId}
        personaName={match?.name || "Persona"}
        relationshipStage={escalationState?.level || 3}
        onUseInChat={handleUseCoachResponse}
        onCoachingGenerated={handleCoachingGenerated}
        onTokensUpdate={handleCoachingTokensUpdate}
        userDraft={currentDraft}
        sessionId={currentSessionId}
        matchTextingStyle={match?.preferred_message_length || match?.texting_style || match?.communication_style}
        // Escalation mode props
        isEscalationMode={currentSessionMode === "escalation"}
        difficulty={selectedDifficulty}
        currentXp={escalationState?.xp || 50}
        currentLevel={escalationState?.level || 1}
        trend={escalationState?.trend || "stable"}
        upstreak={escalationState?.debug?.streak_info?.current_upstreak || 0}
        downstreak={escalationState?.debug?.streak_info?.current_downstreak || 0}
      />

      {/* Escalation Objective Slideout - Using extracted component */}
      <EscalationGuideSlideout
        isOpen={showObjectiveSlideout}
        onClose={() => setShowObjectiveSlideout(false)}
        escalationState={escalationState}
        matchFirstName={matchFirstName}
        personalizeGuideText={personalizeGuideText}
      />
    </div>
  );
};

export default ErosChat;
